import * as React from 'react';
import moment from 'moment';
import { debounce } from 'lodash';
import { scrollToHeading } from '../../helpers/scroll-to-heading';

import { connect, Dispatch } from 'react-redux';
import { ICalendarAppState } from '../../redux-state';
import { setDate, nextMonth, previousMonth, openMiniCalDialog, closeMiniCalDialog, setCurrentMonth, upsertEventFilter } from '../../redux-state/actions/calendar.actions';
import { MiniCalendarFooterViewProps } from './footer.view';
import { MiniCalendarGridItemViewProps } from './grid-item.view';
import { MiniCalendarControlsViewProps } from './controls.view';
import { MiniCalendarGridViewProps } from './grid.view';
import MiniCalendarContainerView from './container.view';
import { focusTrap } from '../../helpers/focus-trap';
import { IEvent, IDay } from '../../interfaces';
import { ICalendar } from '../../interfaces';
import { getCookie, dateCookieName } from '../../helpers/cookie-helper';
import { IEventFilter } from '../../classes/calendar';

const weekdays = moment.weekdays().map(dayName => dayName.toLowerCase());
const today = () => moment().clone().startOf('day');

/** INTERFACES AND TYPES **/

/**
 * Props coming from Store
 */
interface StateToProps {
    currentDate: moment.Moment;
    currentMonth: moment.Moment;
    miniCalDialogOpen: boolean;
    desktopToMobileTransitionWidth: number;
    validMonths: moment.Moment[];
    calendarEvents: ICalendar<IEvent>[];
    currentScrolledDate: moment.Moment;
}

/**
 * Dispatch methods coming from Redux
 */
interface DispatchToProps {
    setDate: (newDate: moment.Moment) => void;
    setCurrentMonth: (newDate: moment.Moment) => void;
    setNextMonth: () => void;
    setPreviousMonth: () => void;
    openMiniCalDialog: (trigger: HTMLButtonElement) => void;
    closeMiniCalDialog: () => void;
}

/**
 * Store and Dispatch props as a single type
 */
export type MiniCalendarProps = StateToProps & DispatchToProps;

/**
 * Mini Calendar internal state
 */
export interface MiniCalendarState {
    nextMonthRef: React.RefObject<HTMLButtonElement>;
    previousMonthRef: React.RefObject<HTMLButtonElement>;
    todayRef: React.RefObject<HTMLButtonElement>;
    footerRef: React.RefObject<HTMLDivElement>;
    gridDates: MiniCalendarGridItemViewProps[];
    activeDate: number;
    dateWasClicked: boolean;
    todayWasClicked: boolean;
    previousMonthWasClicked: boolean;
    shiftFocusToActiveDate: boolean;
    shiftFocusToFirstOfMonth: boolean;
    useModal: boolean;
    firstRef: React.RefObject<HTMLDivElement>;
    currentScrolledDate: moment.Moment;
}

enum KeyCode {
    tab = 9,
    left = 37,
    up = 38,
    right = 39,
    down = 40
}


class MiniCalendar extends React.Component<MiniCalendarProps, MiniCalendarState> {
    private datesMemo: {};
    constructor(props: any) {
        super(props);

        this.handlePreviousMonthClick = this.handlePreviousMonthClick.bind(this);
        this.handleTodayClick = this.handleTodayClick.bind(this);
        this.handleTodayTab = this.handleTodayTab.bind(this);
        this.handleGridKeyEvent = this.handleGridKeyEvent.bind(this);
        this.handleFooterShiftTab = this.handleFooterShiftTab.bind(this);
        this.handleResize = this.handleResize.bind(this);

        this.state = this.initialState;

        let cookieDate = getCookie(dateCookieName);
        if (cookieDate) {
            this.props.setCurrentMonth(moment(cookieDate));
        }

    }

    /** LIFECYCLE METHODS **/

    /**
     * Return JSX to render mini-calendar
     */
    render() {
        const trap = focusTrap(this.state.firstRef, this.state.footerRef, () => this.state.useModal && this.props.miniCalDialogOpen);
        return <MiniCalendarContainerView {...this.props} {...this.state} controlsProps={this.controlsProps} gridProps={this.gridProps} footerProps={this.footerProps} handleKeyDown={trap} />
    }

    /**
     * Shift mini-calendar in and out of modal based on screen size
     */
    componentDidMount() {
        this.handleResize();
        window.addEventListener('resize', debounce(this.handleResize, 50));
    }
    handleResize() {
        const useModal = this.props.desktopToMobileTransitionWidth >= window.innerWidth;
        if (useModal != this.state.useModal) {
            this.setState({
                useModal
            });
        }
        if (this.props.miniCalDialogOpen && !useModal) {
            this.props.closeMiniCalDialog();
        }
    }

    /**
     * Checks whether or not to re-render this component's children
     */
    shouldComponentUpdate(nextProps: MiniCalendarProps) {
        return this.props.miniCalDialogOpen !== nextProps.miniCalDialogOpen ||
            this.props.currentDate.toDate() !== nextProps.currentDate.toDate() ||
            this.props.currentMonth.toDate() !== nextProps.currentMonth.toDate() ||
            this.props.calendarEvents !== nextProps.calendarEvents ||
            !this.props.currentScrolledDate.isSame(nextProps.currentScrolledDate);
    }

    /**
     * Handle update scenarios
     */
    componentDidUpdate(oldProps: MiniCalendarProps) {
        this.handleResize();
        const now = today();

        // track whether we should be shifting focus
        let shiftFocus = false;

        // collect any state changes
        const stateChanges: any = {};

        // get a reference to the grid dates for current month
        const gridDates = this.gridDates(this.props.currentMonth);
        const firstGridIndexWhere = firstIndexFinder(gridDates);

        // if currentMonth changed
        if (this.props.currentMonth != oldProps.currentMonth || this.props.calendarEvents !== oldProps.calendarEvents) {
            // update gridDates
            stateChanges.gridDates = gridDates;

            // update activeDate to first of month
            const activeDate = firstGridIndexWhere(date => date.sameMonth && !!date.click);
            if (activeDate >= 0) {
                stateChanges.activeDate = activeDate;

                // if month was changed via key-command
                if (this.state.shiftFocusToFirstOfMonth) {
                    stateChanges.shiftFocusToFirstOfMonth = false;
                    if (activeDate >= 0) {
                        shiftFocus = true;
                    }
                }
            }

            // check if focus should be shifted to "Next Month" button
            if (this.state.previousMonthWasClicked) {
                stateChanges.previousMonthWasClicked = false;

                // if new currentMonth is same as today.startOf('month')
                if (this.props.currentMonth.isSame(now.startOf('month'))) {
                    const activeDate = firstGridIndexWhere(date => !!date.click && date.sameMonth);
                    if (activeDate >= 0) {
                        stateChanges.activeDate = activeDate;
                        shiftFocus = false;
                    }
                    // manually shift focus
                    this.focusRef(this.state.nextMonthRef);
                }
            }
        }

        // if currentDate changed
        if (!this.props.currentDate.isSame(oldProps.currentDate)) {
            const activeDate = firstGridIndexWhere(date => this.props.currentDate.isSame(date.moment));
            if (activeDate >= 0) {
                stateChanges.activeDate = activeDate;
                if (this.state.dateWasClicked) {
                    stateChanges.dateWasClicked = false;
                }
            }
        }

        // if "today" button was clicked
        if (this.state.todayWasClicked) {
            const activeDate = firstGridIndexWhere(date => now.isSame(date.moment));
            if (activeDate >= 0) {
                stateChanges.todayWasClicked = false;

                // update activeDate
                stateChanges.activeDate = activeDate;

                // and shift focus
                shiftFocus = true;
            }
        }

        if (this.props.miniCalDialogOpen && !oldProps.miniCalDialogOpen) {
            this.state.firstRef.current.focus();
        }

        // commit any changes
        this.commit(stateChanges, shiftFocus);
    }

    /**
     * 
     * @param stateChanges An object which, if it contains any keys matching the state object, will be merged into the new state
     * @param shiftFocus A flag which indicates whether focus should be shifted to the active date after state updates
     */
    commit(stateChanges: any, shiftFocus?: boolean) {
        if (Object.keys(stateChanges).some(key => key in this.state)) {
            this.setState(stateChanges, () => {
                if (shiftFocus) {
                    this.focusActiveDate();
                }
            });
        }
    }

    /** FOCUS CONTROL **/

    /**
     * Shift focus to a RefObject
     */
    focusRef(ref: any) {
        ref.current.focus();
    }

    /**
     * Shift focus to the active date
     */
    focusActiveDate() {
        if (this.state.gridDates) {
            const active = this.state.gridDates[this.state.activeDate];
            if (active.buttonRef.current) {
                this.focusRef(active.buttonRef);
            }
        }
    }

    /**
     * Handle activeDate changes from arrow key events
     */
    handleFocusShift(days: number) {
        if ((days > 0 && this.state.activeDate + days < this.state.gridDates.length) || (days < 0 && this.state.activeDate + days >= this.firstValidDate)) {
            this.setState({
                activeDate: this.state.activeDate + days
            }, this.focusActiveDate);
        }
    }

    /** EVENT HANDLERS **/

    /**
     * Handle key events targeting grid dates
     */
    handleGridKeyEvent(e: React.KeyboardEvent<HTMLButtonElement>) {
        if (e.shiftKey) {
            if (e.keyCode === KeyCode.up || e.keyCode === KeyCode.left) {
                // Shift + Up/Left
                e.preventDefault();
                this.setState({
                    shiftFocusToFirstOfMonth: true
                }, this.props.setPreviousMonth);
            } else if (e.keyCode === KeyCode.down || e.keyCode === KeyCode.right) {
                // Shift + Down/Right
                e.preventDefault();
                this.setState({
                    shiftFocusToFirstOfMonth: true
                }, this.props.setNextMonth);
            } else if (e.keyCode === KeyCode.tab) {
                // Shift + Tab
                e.preventDefault();
                this.focusRef(this.state.todayRef);
            }
        } else {
            if (e.keyCode === KeyCode.up) {
                // Up
                e.preventDefault();
                this.handleFocusShift(-7);
            } else if (e.keyCode === KeyCode.left) {
                // Left
                e.preventDefault();
                this.handleFocusShift(-1);
            } else if (e.keyCode === KeyCode.down) {
                // Down
                e.preventDefault();
                this.handleFocusShift(7);
            } else if (e.keyCode === KeyCode.right) {
                // Right
                e.preventDefault();
                this.handleFocusShift(1);
            } else if (e.keyCode === KeyCode.tab) {
                // Tab
                e.preventDefault();
                this.focusRef(this.state.footerRef);
            }
        }
    }

    /**
     * Focus on the active Next Month button
     */
    handlePreviousMonthClick() {
        this.setState({
            previousMonthWasClicked: true
        }, () => {
            this.props.setPreviousMonth();
            scrollToHeading(moment(this.props.currentMonth.clone().subtract(1, 'month').startOf('month')), true);
        });
    }

    /**
     * Update the date then 
     */
    handleTodayClick() {
        this.setState({
            todayWasClicked: true
        }, () => this.props.setDate(today()));
    }

    handleMonthChange(evt: React.ChangeEvent<HTMLSelectElement>) {
        const selectedMonth = moment(evt.target.value, "X");
        this.setState({
            todayWasClicked: false
        }, () => {
            this.props.setCurrentMonth(selectedMonth.clone().startOf('month'));
        })
    }

    /**
     * Focus on the active Grid Date if key event is `tab`
     */
    handleTodayTab(e: React.KeyboardEvent<HTMLButtonElement>) {
        if (!e.shiftKey && e.keyCode === KeyCode.tab) {
            e.preventDefault();
            this.focusActiveDate();
        }
    }

    /**
     * Focus on the active Grid Date if key event is `shift + tab`
     */
    handleFooterShiftTab(e: React.KeyboardEvent<HTMLDivElement>) {
        if (e.shiftKey && e.keyCode === KeyCode.tab) {
            e.preventDefault();
            this.focusActiveDate();
        }
    }

    /** FACTORIES / ACCESSORS **/

    /**
     * Returns array of props for Mini Calendar Grid Item Views
     */
    gridDates(month: moment.Moment): MiniCalendarGridItemViewProps[] {
        if (!this.datesMemo) {
            // initialize caching
            this.datesMemo = {};
        }
        const memoDates = this.datesMemo[month.toString()];
        //if (!!memoDates) {
        //    // return cached if available
        //    return memoDates;
        //} else {
        const now = today();

        // find first-of-month offset
        const firstIndex = weekdays.indexOf(month.format('dddd').toLowerCase());

        // fill moments list
        let day = month.clone().add(-firstIndex, 'days');
        const endOfMonth = month.clone().endOf('month');
        const moments: moment.Moment[] = [];
        while (day <= endOfMonth) {
            moments.push(day);
            day = day.clone();
            day.add(1, 'day');
        }

        const allEvents: IDay<IEvent>[] = this.props.calendarEvents ? this.props.calendarEvents.reduce((old, curr) => {
            return old.concat(curr.days);
        }, []) : null;

        // fill days list
        const days: MiniCalendarGridItemViewProps[] = moments.map(day => {
            const todayEvents = allEvents ? allEvents.filter(eventDay => {
                return moment(eventDay.date).clone().startOf("day").isSame(day.clone().startOf("day"))
            }) : null;

            const hasEvents = todayEvents && todayEvents.length > 0 ? todayEvents[0].events.length > 0 : false;

            return {
                click: day < now
                    ? undefined
                    : () => {
                        this.setState({
                            dateWasClicked: true
                        }, () => {
                            if (this.props.miniCalDialogOpen) {
                                this.props.closeMiniCalDialog();
                            }
                            this.props.setDate(day);
                        });
                    },
                handleKeyEvent: this.handleGridKeyEvent,
                moment: day,
                day: day.date(),
                buttonRef: React.createRef<HTMLButtonElement>(),
                sameMonth: !(day < month),
                today: day.isSame(now),
                hasEvents: hasEvents,
                isCurrentDate: this.props.currentScrolledDate.isSame(day)
            }
        });

        // cache results
        this.datesMemo[month.toString()] = days;

        return days;
        //}
    }

    /**
     * Return the index of the first clickable date
     */
    get firstValidDate(): number {
        return firstIndexByPredicate(this.state.gridDates, date => !!date.click);
    }

    /**
     * Returns initial state for the Mini Calendar
     */
    get initialState(): MiniCalendarState {
        const currentMonth = this.props.currentMonth;

        // initialize state props
        const nextMonthRef = React.createRef<HTMLButtonElement>();
        const previousMonthRef = React.createRef<HTMLButtonElement>();
        const todayRef = React.createRef<HTMLButtonElement>();
        const footerRef = React.createRef<HTMLDivElement>();
        const gridDates = this.gridDates(currentMonth);
        const activeDate = firstIndexByPredicate(gridDates, date => date.moment.clone().startOf('day').isSame(today()));
        const dateWasClicked = false;
        const todayWasClicked = false;
        const previousMonthWasClicked = false;
        const shiftFocusToActiveDate = false;
        const shiftFocusToFirstOfMonth = false;
        const useModal = false;
        const firstRef = React.createRef<HTMLDivElement>();
        const currentScrolledDate = this.props.currentDate;

        // return state object
        return {
            nextMonthRef,
            previousMonthRef,
            todayRef,
            footerRef,
            gridDates,
            activeDate,
            todayWasClicked,
            dateWasClicked,
            previousMonthWasClicked,
            shiftFocusToActiveDate,
            shiftFocusToFirstOfMonth,
            useModal,
            firstRef,
            currentScrolledDate
        };
    }

    /**
     * Returns props for the Mini Calendar Controls View
     */
    private get controlsProps(): MiniCalendarControlsViewProps {
        return {
            currentMonth: this.props.currentMonth,
            handleNextMonthClick: this.props.setNextMonth,
            handlePreviousMonthClick: this.handlePreviousMonthClick,
            handleTodayClick: this.handleTodayClick,
            handleMonthChange: this.handleMonthChange.bind(this),
            nextMonthRef: this.state.nextMonthRef,
            previousMonthRef: this.state.previousMonthRef,
            todayRef: this.state.todayRef,
            handleTodayTab: this.handleTodayTab,
            displayPreviousMonth: this.props.currentMonth > today().startOf('month'),
            validMonths: this.props.validMonths
        }
    }

    /**
     * Returns props for the Mini Calendar Grid View
     */
    private get gridProps(): MiniCalendarGridViewProps {
        return {
            gridDates: this.state.gridDates
        };
    }

    /**
     * Returns props for the Mini Calendar Footer View
     */
    private get footerProps(): MiniCalendarFooterViewProps {
        return {
            footerRef: this.state.footerRef,
            handleKeyDown: this.handleFooterShiftTab
        }
    }
}

/** REDUX SETUP **/

/**
 * Map global store to state-half of MiniCalendarProps
 */
function mapStateToProps(state: ICalendarAppState): StateToProps {
    const monthEvents = (state.calendar.filteredEvents.filter(eventMonth => {
        return state.calendar.currentMonth.clone().startOf("month").isSame(eventMonth.monthMoment.clone().startOf("month")) ||
            state.calendar.currentMonth.clone().startOf("month").isSame(eventMonth.monthMoment.clone().add(1, "month"))
    })).map(month => month.events);

    return {
        currentDate: state.calendar.currentDate,
        currentMonth: state.calendar.currentMonth,
        miniCalDialogOpen: state.calendar.miniCalDialogOpen,
        desktopToMobileTransitionWidth: parseInt(state.calendarSettings.desktopToMobileTransitionWidth),
        validMonths: state.calendarSettings.monthsOnCalendar,
        calendarEvents: monthEvents ? monthEvents : null,
        currentScrolledDate: state.calendar.currentScrolledDate
    }
}

/**
 * Map global dispatchers to dispatch-half of MiniCalendarProps
 */
function mapDispatchToProps(dispatch: Dispatch): DispatchToProps {
    return {
        setDate: (newDate: moment.Moment) => {
           dispatch(setDate(newDate));
        },
        setCurrentMonth: (newDate: moment.Moment) => dispatch(setCurrentMonth(newDate)),
        setNextMonth: () => dispatch(nextMonth()),
        setPreviousMonth: () => dispatch(previousMonth()),
        openMiniCalDialog: (trigger: HTMLButtonElement) => dispatch(openMiniCalDialog(trigger)),
        closeMiniCalDialog: () => dispatch(closeMiniCalDialog())
    }
}

export default connect<StateToProps, DispatchToProps>(mapStateToProps, mapDispatchToProps)(MiniCalendar);

/** UTILITIES **/

/**
 * Higher-order function to curry the index finder
 * @param haystack 
 */
function firstIndexFinder<T>(haystack: T[]) {
    return function find(predicate: (date: T) => boolean) {
        return firstIndexByPredicate(haystack, predicate);
    }
}

/**
 * Finds the index of the first entry satisfying the predicate function
 * 
 * @param haystack the array of grid item view props to search
 * @param predicate the function to match
 */
function firstIndexByPredicate<T>(haystack: T[], predicate: (date: T) => boolean): number {
    for (let i = 0; i < haystack.length; i++) {
        if (predicate(haystack[i])) {
            return i;
        }
    }
    return -1;
}